/**
 * ScheduleItem
 * - displays a ListItem populated with timestamp and schedule settings
 * - consumes the TimePicker and EditSettings components
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { TimePicker } from './time-picker';
import { EditSettings } from './edit-settings';
import { MyScheduleSetting, TimeOfDay } from '../../types';
import {
  Celsius,
  TemperatureUnit,
} from '../../../../../../system/models/temperatureUnits';
import { SetpointLimitSettings } from '../../../../../units/types/setpointLimitSettings';

interface ScheduleItemProps {
  time?: TimeOfDay;
  id: string;
  setting?: MyScheduleSetting;
  setpointLimitSettings: SetpointLimitSettings; // TODO: Calvin ask Peter if all thermostats contain max, min setpoint limit values
  preferredUnits: TemperatureUnit;
  templateValues?: boolean;
  handleEdit: (
    setting: MyScheduleSetting,
  ) => (oldTime: TimeOfDay, newTime?: TimeOfDay) => void;
}

export function ScheduleItem(props: ScheduleItemProps) {
  const {
    time,
    setting,
    handleEdit,
    preferredUnits,
    setpointLimitSettings,
    id,
    templateValues = false,
  } = props;

  const [newHeatValue, setHeatValue] = useState<number | undefined>(
    setting?.minimum,
  );
  const [newCoolValue, setCoolValue] = useState<number | undefined>(
    setting?.maximum,
  );

  const [anchorEl, setAnchorEl] = useState<
    Record<string, HTMLButtonElement | null>
  >({ heating: null, cooling: null });

  const theme = useTheme();

  const open = (mode: string) => Boolean(anchorEl[mode]);

  useEffect(() => {
    setHeatValue(setting?.minimum);
    setCoolValue(setting?.maximum);
  }, [setting]);

  const handleClick = (
    mode: string,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl({ [mode]: event.currentTarget });
  };

  const handleClose = (mode: string, newSetting: MyScheduleSetting) => {
    setAnchorEl({ [mode]: null });
    if (time) {
      handleEdit({ ...setting, maximum: newCoolValue, minimum: newHeatValue })(
        time,
      );
    }
  };

  const handleChange = (mode: string, value: number) => {
    const proposed = {
      minimum: mode === 'heating' ? value : newHeatValue,
      maximum: mode === 'cooling' ? value : newCoolValue,
    };
    const deadband = preferredUnits === Celsius ? 2 : 3.6;
    if (
      proposed.minimum !== undefined &&
      proposed.maximum !== undefined &&
      proposed.maximum - proposed.minimum < deadband
    ) {
      if (mode === 'heating') proposed.maximum = proposed.minimum + deadband;
      else proposed.minimum = proposed.maximum - deadband;
    }
    for (const key of ['minimum', 'maximum'] as const) {
      const current = proposed[key];
      const min =
        key === 'minimum'
          ? setpointLimitSettings.minHeatSetpointLimit
          : setpointLimitSettings.minCoolSetpointLimit;
      const max =
        key === 'minimum'
          ? setpointLimitSettings.maxHeatSetpointLimit
          : setpointLimitSettings.maxCoolSetpointLimit;
      if (
        current !== undefined &&
        ((!templateValues && (min == null || max == null)) ||
          (min != null && current < min) ||
          (max != null && current > max))
      )
        return;
    }

    setHeatValue(proposed.minimum);
    setCoolValue(proposed.maximum);
  };

  return (
    (time && (
      <>
        <ListItem
          key={time}
          sx={{
            pl: 1,
            justifyContent: 'space-evenly',
          }}
        >
          {/* <Material Time Picker - TODO: Calvin: Need alternate IOS style version */}
          <TimePicker
            time={time}
            // handleEdit={handleEdit({
            //   maximum: newCoolValue,
            //   minimum: newHeatValue,
            // })}
            handleEdit={handleEdit({
              ...setting,
            })}
          />
          <EditSettings
            id={id + '-heating'}
            mode={'heating'}
            templateValues={templateValues}
            handleChange={handleChange}
            maxHeatSetpointLimit={setpointLimitSettings?.maxHeatSetpointLimit}
            minHeatSetpointLimit={setpointLimitSettings?.minHeatSetpointLimit}
            value={newHeatValue}
            open={open('heating')}
            anchorEl={anchorEl.heating}
            handleClick={handleClick}
            handleClose={handleClose}
            theme={theme}
          />
          <EditSettings
            id={id + '-cooling'}
            mode={'cooling'}
            templateValues={templateValues}
            handleChange={handleChange}
            maxCoolSetpointLimit={setpointLimitSettings?.maxCoolSetpointLimit}
            minCoolSetpointLimit={setpointLimitSettings?.minCoolSetpointLimit}
            value={newCoolValue}
            open={open('cooling')}
            anchorEl={anchorEl.cooling}
            handleClick={handleClick}
            handleClose={handleClose}
            theme={theme}
          />
        </ListItem>
        <Divider />
      </>
    )) || (
      <ListItem key={'SCHEDULE_ERROR'}>
        <ListItemText primary={'Error - Invalid Timestamp'} />
      </ListItem>
    )
  );
}
